import React, { useRef, useState, useMemo } from 'react';
import TravelRouteCard from 'v3/components/TravelRouteCard';
import {
  insertWaypointsIntoPoints,
  normalizeWaypoints,
} from 'v3/components/TravelRouteCard/utils/points';
import { getRouteWaypointsFromPoints } from 'v3/components/TravelRouteCard/utils/routeMachineCard';
import Text from 'v3/components/Text';
import { useSnackbar } from 'v3/components/Snackbar';
import * as Controller from './controller';
import moment from 'moment';
import { cooperplaceApi } from 'services/api';
import { validateDriver } from 'utils/travel';
import { ConflictDriverModal, MinimumMarginModal } from 'v3/pages/Travel/shared';
import { useModal } from 'hooks';
import { useContext } from 'react';
import { BlockedTravelContext } from 'contexts/BlockedTravelProvider';
import { toReplacementPayload } from 'v3/components/FreightValues/utils';
import { useInsuranceValue } from 'contexts/InsuranceValueContext';
import { STATUS_CODE } from '../../../Travel/Details/Map/Progress';
import { UNDER_GRO_ANALYSIS_SLUG } from 'v3/utils/constants'

function getRouteSaveErrorMessage(error) {
  const responseData = error?.response?.data;

  if (!responseData) return 'Falha ao salvar rota';

  if (typeof responseData === 'string') return responseData;

  if (responseData.message) return responseData.message;
  if (responseData.error) return responseData.error;

  const errors = responseData.errors;
  if (Array.isArray(errors) && errors.length) {
    return errors
      .map((item) => item?.message || item?.error || item)
      .filter(Boolean)
      .join('\n');
  }

  if (Array.isArray(responseData) && responseData.length) {
    return responseData
      .map((item) => item?.message || item?.error || item)
      .filter(Boolean)
      .join('\n');
  }

  return 'Falha ao salvar rota';
}

export default function Locale({
  originsData = [],
  destinationsData = [],
  loadId,
  getLoad,
  setLoad,
  groLoadData,
  countryList,
  load,
  preSMData,
  isDisabledGROAnalysis,
  freightValuesRef,
  freightValuesReplaceRef,
  updateFreightValues,
  saveTollsPlaza,
  tollsPlazaInfos,
  setTollsPlazaInfos,
  getTollsPlazaByLoadId,
  loadingCard = false,

}) {
  const { isTravelBlocked, isMarginAbove } = useContext(BlockedTravelContext);
  const { updateInsuranceValueByLoadId } = useInsuranceValue();
  const snackbar = useSnackbar();
  const [validateDriverProps, setValidateDriverProps] = useState({
    conflictTravels: [],
    driver: {},
  });
  const [saveParams, setSaveParams] = useState({});
  const [confirmModalValidator, setConfirmModalValidator] = useState({
    confirmMinimiumMargin: false,
    passDriverValidation: false,
  });
  const pendingRouteSaveResolverRef = useRef(null);
  const lowerMarginModal = useModal();
  const driverAvailableModal = useModal();
  const routePointsForEditCheck = useMemo(() => {
    const currentOrigins = Array.isArray(load?.origins) && load.origins.length > 0
      ? load.origins
      : Array.isArray(originsData)
        ? originsData
        : [];

    const currentDestinations = Array.isArray(load?.destinations) && load.destinations.length > 0
      ? load.destinations
      : Array.isArray(destinationsData)
        ? destinationsData
        : [];

    return [
      ...currentOrigins,
      ...currentDestinations,
    ];
  }, [load?.origins, load?.destinations, originsData, destinationsData]);

  const hasAnyEditablePointInRoute = useMemo(
    () => routePointsForEditCheck.some((point) => {
      const hasArrival = Boolean(point?.arrivalTime || point?.arrival_time);
      const hasEffective = Boolean(point?.effectiveTime || point?.effective_time);
      return !(hasArrival || hasEffective);
    }),
    [routePointsForEditCheck],
  );

  const hasActiveSM = [
    'CREATED_SM',
    'CREATED_PRE_SM',
    'EFFECT_PRE_SM'
  ].includes(preSMData?.statusPreSM?.slug);

  const isBlockedByTravelStatusForRoute = useMemo(() => {
    const code = load?.travel?.travelStatus?.code;
    const blockedStatuses = [
      STATUS_CODE.DOCS_ISSUED,
      STATUS_CODE.WAITING_ISSUING,
      STATUS_CODE.IN_TRAVEL,
      STATUS_CODE.ANALYSIS,
      STATUS_CODE.FINISHED,
      STATUS_CODE.CANCELLED,
      STATUS_CODE.OVERFLOWED,
    ];

    return Boolean(code && blockedStatuses.includes(code));
  }, [load?.travel?.travelStatus?.code]);

  const canEditRoute = useMemo(() => {
    const groSlug = load?.travel?.riskApproved
    if (groSlug === UNDER_GRO_ANALYSIS_SLUG) {
      return false
    }

    if (isDisabledGROAnalysis) return false;
    if (isTravelBlocked) return false;
    if (isBlockedByTravelStatusForRoute) return false;
    if (!hasAnyEditablePointInRoute) return false;

    return true;
  }, [
    load?.travel?.riskApproved,
    isDisabledGROAnalysis,
    isTravelBlocked,
    isBlockedByTravelStatusForRoute,
    hasAnyEditablePointInRoute,
  ]);

  const points = useMemo(() => {
    const pts = [];
    const currentOrigins =
      load?.origins?.length > 0 ? load.origins : originsData;
    const currentDestinations =
      load?.destinations?.length > 0 ? load.destinations : destinationsData;
    const [origin] = currentOrigins || [];
    pts.push(origin);
    if (currentDestinations?.length > 0) {
      pts.push(...currentDestinations);
    }

    const waypoints = normalizeWaypoints(
      load?.polylineWaypoints || load?.polyline_waypoints,
    );
    return insertWaypointsIntoPoints(pts.filter(Boolean), waypoints);
  }, [
    originsData,
    destinationsData,
    load?.origins,
    load?.destinations,
    load?.polylineWaypoints,
    load?.polyline_waypoints,
  ]);

  function handleChangeRoute({ polyline, polylineWaypoints }) {
    setLoad(prev => {
      const nextPolyline = polyline;
      const nextWaypoints =
        polylineWaypoints === undefined
          ? prev.polylineWaypoints
          : polylineWaypoints;

      const samePolyline =
        (prev?.polylineRoute || prev?.polyline_route || prev?.polilyneRoute || null) ===
        (nextPolyline || null);
      const sameWaypoints =
        JSON.stringify(prev?.polylineWaypoints || prev?.polyline_waypoints || null) ===
        JSON.stringify(nextWaypoints || null);

      if (samePolyline && sameWaypoints) {
        return prev;
      }

      return {
        ...prev,
        polylineRoute: nextPolyline,
        polylineWaypoints: nextWaypoints,
      };
    });
  }

  async function validatePolicyDestinations({ origin, destinations }) {
    try {
      await cooperplaceApi.post(
        `policies/${groLoadData.policyId}/validator`,
        {
          type: ['route'],
          operationId: groLoadData?.operationId,
          load: {
            productId: groLoadData?.productId,
            loadOrigins: {
              cityId: origin?.city?.id,
            },
            loadDestinations: destinations.map(dest => ({
              cityId: dest?.city?.id,
            })),
          },
        }
      );
      return true;
    } catch (error) {
      let message = 'Erro ao validar rota!';
      if (error?.response?.data?.error) {
        message = error?.response?.data?.error;
      } else if (error?.response?.data?.errors?.length) {
        message = error?.response?.data?.errors.map(item => (
          <>
            <Text type="body" weight={500}>{item}</Text>
            

          </>
        ));
      }
      snackbar.show(
        <Text type="body" weight={500}>
          {message}
        </Text>,
        {
          type: 'error',
        }
      );
    }

    return false;
  }

  async function validateTravelsDriver({ origin, destinations }) {
    try {
      const lastDestination = destinations?.length ? destinations[destinations.length - 1] : null;
      const validateDriverParams = {
        originScheduledTime: moment(origin.start_schedule).toISOString(),
        destinationScheduledTime: moment(lastDestination?.scheduled_time).toISOString(),
        driverId: load?.travel?.driverId,
        travelId: load?.travel?.id,
      };

      const { travels } = await validateDriver(
        validateDriverParams
      );
      return travels;
    } catch (err) {

      snackbar.show(
        <Text>{'Falha ao validar motorista em viagem'}</Text>,
        { type: 'error' }
      );
    }
  }

  async function validateMargin({ origin, destinations }) {
    try {
      const addressParams = {
        origin: {
          cityId: origin.city?.id,
          city: origin?.city?.name,
          province: origin?.city?.province?.uf,
          countrySlug: origin?.country.abbreviation,
        },
        destinations: destinations.map(destination => ({
          cityId: destination?.city?.id,
          city: destination?.city?.name,
          province: destination?.city?.province?.uf,
          countrySlug: destination?.country.abbreviation,
        })),
      };
      return await isMarginAbove(addressParams);
    } catch (error) {

      snackbar.show(
        <Text>Erro ao validar motorista e previsão de margem</Text>,
        {
          type: 'error',
        }
      );
      return false;
    }
  }

  async function handleSave(location, validations = {}) {
    const reloadLoad = async () => {
      if (getLoad) await getLoad();
    };

    const {
      origin: originFromProps,
      destinations: destinationsFromProps,
      points,
      polyline,
      polylineWaypoints
    } = location;

    const origin = points?.length ? points[0] : originFromProps;
    const destinations = points?.length ? points.slice(1) : destinationsFromProps;
    const poiDestinations = (destinations || []).filter(
      dest => dest?.type?.id !== 'via'
    );
    const polylineWaypointsPayload =
      typeof polylineWaypoints === 'string' && polylineWaypoints
        ? polylineWaypoints
        : getRouteWaypointsFromPoints(points || []) || null;

    if (groLoadData !== null) {
      if (!(await validatePolicyDestinations({ origin, destinations: poiDestinations }))) {
        await reloadLoad();
        return false;
      }
    }

    const passDriverValidation = validations?.passDriverValidation || confirmModalValidator?.passDriverValidation;
    const confirmMinimiumMargin = validations?.confirmMinimiumMargin || confirmModalValidator?.confirmMinimiumMargin;
    if (load?.travel?.driverId && !passDriverValidation) {
      const travels = await validateTravelsDriver({ origin, destinations: poiDestinations });
      if (travels.length >= 2) {
        snackbar.show(
          <Text>
            {`O motorista está vinculado nas seguintes viagens nesse período: "${travels.join(', ')}"`}
          </Text>,
          {
            type: 'error',
          }
        );
        return;
      }

      if (travels.length === 1) {
        setValidateDriverProps(old => ({
          ...old,
          conflictTravels: travels,
          driver: load?.travel?.driver
        }));
        setSaveParams(location);
        driverAvailableModal.open();
        return new Promise((resolve) => {
          pendingRouteSaveResolverRef.current = resolve;
        });
      }
    }

    if (!confirmMinimiumMargin && load?.travel?.id) {
      const isMarginERPAbove = await validateMargin({ origin, destinations: poiDestinations });
      if (isMarginERPAbove) {
        setSaveParams(location);
        lowerMarginModal.open();
        return new Promise((resolve) => {
          pendingRouteSaveResolverRef.current = resolve;
        });
      }
    }

    try {
      const toDestination = (destination) => ({
        id: destination.id,
        address: destination.address,
        number: destination.number || null,
        complement: destination.complement || null,
        zipCode: destination.zip_code || null,
        lat: destination.lat,
        lng: destination.lng,
        cityId: destination.city?.id,
        city: destination?.city?.name,
        province: destination?.province,
        countrySlug: destination?.country?.abbreviation,
        neighborhood: destination.neighborhood || null,
        formattedAddress: destination.formatted_address || null,
        scheduledTime: moment(destination.scheduled_time).format('YYYY-MM-DD HH:mm:ss'),
        startSchedule: moment(destination.start_schedule).format('YYYY-MM-DD HH:mm:ss'),
        clientId: destination.clientId || null,
      });

      const payload = {
        totalDistanceInKm: load?.totalDistanceInKm || 0,
        polylineRoute: polyline,
        polylineWaypoints: polylineWaypointsPayload,
        origins: [toDestination(origin)],
        destinations: (poiDestinations || []).map(dest => ({
          ...toDestination(dest),
          action: dest.type?.id,
        })),
      };

      await Controller.save(loadId, payload);

      await saveTollsPlaza(tollsPlazaInfos, loadId, load?.travel?.id)

      const insuranceValue = await updateInsuranceValueByLoadId(loadId);
      if (insuranceValue !== null) {
        const insuranceCoopercargaValue =
          freightValuesRef?.current?.setInsuranceValueBenner(insuranceValue);
        const freightValues = await freightValuesRef?.current?.getValues();
        const freightValuesData = {
          ...freightValues,
          discounts: {
            ...freightValues.discounts,
            coopercargaInsurance: insuranceCoopercargaValue,
          },
        }

        let replacementPayload = null
        if (load?.isReplacement) {
          const insuranceReplacementValue =
            freightValuesReplaceRef?.current?.setInsuranceValueBenner(insuranceValue);

          const replacementFreightValues = await freightValuesReplaceRef?.current?.getValues();

          replacementPayload = toReplacementPayload({
            ...replacementFreightValues,
            discounts: {
              ...replacementFreightValues.discounts,
              coopercargaInsurance: insuranceReplacementValue,
            },
          })
        }

        await updateFreightValues(freightValuesData, replacementPayload);
      }

      setConfirmModalValidator({
        passDriverValidation: false,
        confirmMinimiumMargin: false,
      });

      snackbar.show(<Text>Rota salva com sucesso!</Text>, { type: 'success' });

      setLoad(prev => ({
        ...prev,
        origins: [origin],
        destinations: poiDestinations || [],
        polylineRoute: polyline,
        polylineWaypoints: polylineWaypointsPayload,
      }));

      if (getLoad) await getLoad();
      if (getTollsPlazaByLoadId) await getTollsPlazaByLoadId();
      return true;
    } catch (err) {
      const message = getRouteSaveErrorMessage(err);
      snackbar.show(
        <Text style={{ whiteSpace: 'pre-line' }}>{message}</Text>,
        { type: 'error' },
      );
      await reloadLoad();
      return false;
    }
  }

  const resolvePendingRouteSave = (result) => {
    if (typeof pendingRouteSaveResolverRef.current !== 'function') return;
    pendingRouteSaveResolverRef.current(result);
    pendingRouteSaveResolverRef.current = null;
  };

  const takePendingRouteSaveResolver = () => {
    const resolver = pendingRouteSaveResolverRef.current;
    pendingRouteSaveResolverRef.current = null;
    return resolver;
  };

  const handleConfirmLowerMargin = async value => {
    const resolveCurrentSave = takePendingRouteSaveResolver();
    setConfirmModalValidator(old => ({ ...old, ...value }));
    lowerMarginModal.close();
    const result = await handleSave(saveParams, {
      confirmMinimiumMargin: true,
    });
    if (typeof resolveCurrentSave === 'function') {
      resolveCurrentSave(result);
    }
  };

  const handleCloseLowerMargin = () => {
    setConfirmModalValidator(old => ({
      ...old,
      confirmMinimiumMargin: false,
    }));
    lowerMarginModal.close();
    resolvePendingRouteSave(undefined);
  };

  const handleConfirmDriverConflict = async () => {
    const resolveCurrentSave = takePendingRouteSaveResolver();
    setConfirmModalValidator(old => ({
      ...old,
      passDriverValidation: true,
    }));
    driverAvailableModal.close();
    const result = await handleSave(saveParams, {
      passDriverValidation: true,
    });
    if (typeof resolveCurrentSave === 'function') {
      resolveCurrentSave(result);
    }
  };

  const handleCloseDriverConflict = () => {
    setConfirmModalValidator(old => ({
      ...old,
      passDriverValidation: false,
    }));
    driverAvailableModal.close();
    resolvePendingRouteSave(undefined);
  };

  async function onChangeAddress({ origin, destinations, polylineWaypoints, polyline }) {
    setLoad(prev => ({
      ...prev,
      origins: [origin],
      destinations,
      polylineRoute: polyline === undefined ? prev.polylineRoute : polyline,
      polylineWaypoints:
        polylineWaypoints === undefined
          ? prev.polylineWaypoints
          : polylineWaypoints,
    }));
  }

  const validateRetroactive = useMemo(() => {
    if (!load?.costCenter) return false;
    return load.costCenter?.limitSchedule;
  }, [load?.costCenter]);

  const handleSavePoints = async (nextPoints = [], metadata = {}) => {
    const pointsList = (nextPoints || []).filter(Boolean);
    const businessPoints = pointsList.filter(
      point => point?.type?.id !== 'via' && point?.action?.id !== 'via',
    );

    const [origin, ...destinations] = businessPoints;
    if (!origin || destinations.length === 0) {
      snackbar.show(<Text>A rota precisa ter no mínimo dois pontos de interesse.</Text>, { type: 'error' });
      return false;
    }

    return handleSave({
      points: pointsList,
      origin,
      destinations,
      polyline: metadata?.polylineRoute || load?.polylineRoute || load?.polyline_route || load?.polilyneRoute || null,
      polylineWaypoints:
        metadata?.polylineWaypoints === undefined
          ? load?.polylineWaypoints || load?.polyline_waypoints || null
          : metadata?.polylineWaypoints,
    });
  };

  return (
    <>
      <TravelRouteCard
        points={points}
        externalLoading={loadingCard}
        showEditActions
        hasActiveSM={hasActiveSM}
        canEditRoute={canEditRoute}
        showStatusBlockWarning={isBlockedByTravelStatusForRoute}
        load={load}
        travelData={load?.travel}
        tollsPlazaInfos={tollsPlazaInfos}
        setTollsPlazaInfos={setTollsPlazaInfos}
        calculateTollsBeforeEditing={false}
        validateRetroactive={validateRetroactive}
        externalPolylineRoute={
          load?.polylineRoute ||
          load?.polyline_route ||
          load?.polilyneRoute ||
          null
        }
        onChangeRoute={handleChangeRoute}
        onChangeAddress={onChangeAddress}
        onSavePoints={handleSavePoints}
      />

      <MinimumMarginModal
        isOpen={lowerMarginModal.isOpen}
        onClose={handleCloseLowerMargin}
        confirm={handleConfirmLowerMargin}
      />
      <ConflictDriverModal
        isOpen={driverAvailableModal.isOpen}
        onClose={handleCloseDriverConflict}
        onConfirm={handleConfirmDriverConflict}
        travels={validateDriverProps?.conflictTravels}
        travelData={{ driver: validateDriverProps.driver }}
      />
    </>
  );
}
